import { authTables } from "@convex-dev/auth/server";
import { defineSchema, defineTable } from "convex/server";
import { type Infer, v } from "convex/values";

// ============================================
// ENUMS - TYPE D'ÉVALUATION
// ============================================

export enum TypeEvalEnum {
  INBASKET = "inbasket",
  FORM = "form",
  ENT_STRUCT = "fairview",
  RESULTATS = "resultats", // conteneur d'affichage — déclaré P1, créé seulement P4/P5
}

// ============================================
// ANCIENS IDENTIFIANTS (renommage FairView / PC Fit)
// ============================================
// Valeurs stockées avant le renommage, réécrites par
// `migrations:renommerIdentifiants*`. Les validateurs les acceptent encore pour
// que le push passe sur une base non migrée ; le code ne les lit plus. Retirées
// au narrow (PR D), une fois la prod migrée.
export const ANCIEN_TYPE_EVAL_FAIRVIEW = "ent-struct";
export const ANCIEN_TEMPLATE_PC_FIT = "fit-culturel";
export const ANCIEN_TEMPLATE_REFERENTIEL_PC_FIT = "referentiel-fit-culturel";
export const ANCIENNE_CIBLE_PC_FIT = "fit-culturel";
export const ANCIENNE_APPLICATION_PC_FIT = "fit-culturel";
export const ANCIENNE_APPLICATION_FAIRVIEW = "ent-struct";

export const typeEvalValidator = v.union(
  v.literal(TypeEvalEnum.INBASKET),
  v.literal(TypeEvalEnum.FORM),
  v.literal(TypeEvalEnum.ENT_STRUCT),
  v.literal(TypeEvalEnum.RESULTATS),
  v.literal(ANCIEN_TYPE_EVAL_FAIRVIEW), // legacy, retiré en PR D
);

// ============================================
// ENUMS - STATUT ÉVALUATION (global + analyse fusionnés)
// ============================================

export enum StatutGlobalEnum {
  NON_COMMENCE = "non_commence",
  EN_COURS = "en_cours",
  SOUMIS = "soumis",
  ANALYSE_EN_COURS = "analyse_en_cours",
  TERMINE = "termine",
  ERREUR_ANALYSE = "erreur_analyse",
}

export const statutGlobalValidator = v.union(
  v.literal(StatutGlobalEnum.NON_COMMENCE),
  v.literal(StatutGlobalEnum.EN_COURS),
  v.literal(StatutGlobalEnum.SOUMIS),
  v.literal(StatutGlobalEnum.ANALYSE_EN_COURS),
  v.literal(StatutGlobalEnum.TERMINE),
  v.literal(StatutGlobalEnum.ERREUR_ANALYSE),
);

/**
 * Statut agrégé d'un parcours, dénormalisé dans `parcoursEvaluations.statutCache`.
 * Exporté pour que les projections exposées aux clients réutilisent la MÊME
 * union que la table — sans quoi les deux dérivent en silence.
 */
export const statutCacheValidator = v.union(
  v.literal("non_commence"),
  v.literal("en_cours"),
  v.literal("termine"),
  v.literal("archive"),
);

/** Vrai si le candidat a soumis (évaluation non modifiable) */
export function estEvaluationSoumise(statut: StatutGlobalEnum): boolean {
  return (
    statut !== StatutGlobalEnum.NON_COMMENCE &&
    statut !== StatutGlobalEnum.EN_COURS
  );
}

// ============================================
// ENUMS - STATUT NOTATION (cycle de vie d'une note par évaluateur — dédié)
// ============================================
// Ne réutilise PAS statutGlobalValidator : un évaluateur humain n'a pas de
// phase « analyse LLM ». evaluation.statut reste le statut candidat.

export enum StatutNotationEnum {
  NON_COMMENCEE = "non_commencee",
  EN_COURS = "en_cours",
  TERMINEE = "terminee",
  ERREUR = "erreur",
  ANNULEE = "annulee",
}

export const statutNotationValidator = v.union(
  v.literal(StatutNotationEnum.NON_COMMENCEE),
  v.literal(StatutNotationEnum.EN_COURS),
  v.literal(StatutNotationEnum.TERMINEE),
  v.literal(StatutNotationEnum.ERREUR),
  v.literal(StatutNotationEnum.ANNULEE),
);

// ============================================
// ACTEUR DE NOTATION (humain SANS userId, IA SANS jobId)
// ============================================
// jobId est un niveau exécution → il vit sur le document `notation`, pas ici.

// `humain` = note un CANDIDAT. `referent` = décrit son ORGANISATION (référentiel
// d'entreprise) ; il ne note personne. Deux notions, deux noms — cf. plan
// référentiel §3.5. Comme `humain`, jamais fusionné par `acteurEquivalent` :
// un parcoursEvaluateur par référent.
const acteurNotationValidator = v.union(
  v.object({
    kind: v.literal("humain"),
    nom: v.optional(v.string()),
    email: v.optional(v.string()),
  }),
  v.object({ kind: v.literal("ia"), model: v.string() }),
  v.object({ kind: v.literal("system") }),
  v.object({ kind: v.literal("referent") }),
);

// ============================================
// ENUMS - STATUT CAMPAGNE
// ============================================

export enum StatutCampagneEnum {
  BROUILLON = "brouillon",
  ACTIVE = "active",
  TERMINEE = "terminee",
}

export const statutCampagneValidator = v.union(
  v.literal(StatutCampagneEnum.BROUILLON),
  v.literal(StatutCampagneEnum.ACTIVE),
  v.literal(StatutCampagneEnum.TERMINEE),
);

// ============================================
// ENUMS - TEMPLATES FORM
// ============================================

// FIT_CULTUREL et les REFERENTIEL_* sont des templates DISTINCTS, pas un
// (plan référentiel §3.1) : `getNotationConfig` est keyée (typeEval, template).
// L'éval candidat fit-culturel est `system`/`unique` ; une éval référentiel est
// `humain`/`moyenne-referentiel`. Bénéfice de bord : tout listing filtrant par
// template ignore naturellement les référentiels.
export enum TemplateFormEnum {
  AUTODIAG = "autodiag",
  EGOGRAMME = "egogramme",
  PAMI = "pami",
  COPSOQ3 = "copsoq3",
  FIT_CULTUREL = "pc-fit", // passation candidat Q-sort
  /**
   * Évaluation-référentiel, une par CIBLE : les attentes du poste (PAMI) et la
   * culture de l'entreprise (fit-culturel) sont deux référentiels distincts,
   * portés par deux documents `evaluations` que rien ne distinguait avant ces
   * deux templates — il fallait remonter à `campagnes.referentiels[]`.
   */
  REFERENTIEL_PAMI = "referentiel-pami",
  REFERENTIEL_FIT_CULTUREL = "referentiel-pc-fit",
}

const templateFormValidator = v.union(
  v.literal(TemplateFormEnum.AUTODIAG),
  v.literal(TemplateFormEnum.EGOGRAMME),
  v.literal(TemplateFormEnum.PAMI),
  v.literal(TemplateFormEnum.COPSOQ3),
  v.literal(TemplateFormEnum.FIT_CULTUREL),
  v.literal(TemplateFormEnum.REFERENTIEL_PAMI),
  v.literal(TemplateFormEnum.REFERENTIEL_FIT_CULTUREL),
  v.literal(ANCIEN_TEMPLATE_PC_FIT), // legacy, retiré en PR D
  v.literal(ANCIEN_TEMPLATE_REFERENTIEL_PC_FIT), // legacy, retiré en PR D
);

// Version du questionnaire COPSOQ III : même structure (questions Q01–Q54,
// échelles, domaines), formulations propres à un public. Choisie par le
// super-admin sur l'organisation, figée sur la campagne à sa création, recopiée
// dans chaque évaluation. Catalogue (libellés, parties 1 et 3) :
// `packages/utils/src/copsoq3Versions.ts`.
export enum VersionCopsoq3Enum {
  UNIVERSELLE = "universelle",
  POMPIERS = "pompiers",
}

export const versionCopsoq3Validator = v.union(
  v.literal(VersionCopsoq3Enum.UNIVERSELLE),
  v.literal(VersionCopsoq3Enum.POMPIERS),
);

/** Cible d'un référentiel : quel test du candidat il pondère. */
export type CibleReferentiel = "pami" | "pc-fit";

/** Le template d'évaluation-référentiel d'une cible donnée. */
export const TEMPLATE_REFERENTIEL_PAR_CIBLE: Record<
  CibleReferentiel,
  TemplateFormEnum
> = {
  pami: TemplateFormEnum.REFERENTIEL_PAMI,
  "pc-fit": TemplateFormEnum.REFERENTIEL_FIT_CULTUREL,
};

/**
 * Vrai pour toute évaluation-référentiel, quelle que soit sa cible.
 *
 * À utiliser PARTOUT plutôt qu'une égalité sur un template : un test qui
 * n'accepterait qu'une des cibles ferait retomber l'autre dans le fallback
 * `system` de `getNotationConfig`, et ses référentiels cesseraient
 * silencieusement d'être notés par leurs référents.
 */
export function estTemplateReferentiel(template: string | undefined): boolean {
  return (
    template === TemplateFormEnum.REFERENTIEL_PAMI ||
    template === TemplateFormEnum.REFERENTIEL_FIT_CULTUREL
  );
}

/** La cible portée par un template de référentiel, `undefined` sinon. */
export function cibleDepuisTemplate(
  template: string | undefined,
): CibleReferentiel | undefined {
  if (template === TemplateFormEnum.REFERENTIEL_PAMI) return "pami";
  if (template === TemplateFormEnum.REFERENTIEL_FIT_CULTUREL) return "pc-fit";
  return undefined;
}

// ============================================
// ENUMS - TEMPLATES INBASKET
// ============================================

export enum TemplateInbasketEnum {
  DEFAULT = "default",
  RAPIDE = "rapide",
  // Futurs templates à ajouter ici
}

const templateInbasketValidator = v.union(
  v.literal(TemplateInbasketEnum.DEFAULT),
  v.literal(TemplateInbasketEnum.RAPIDE),
);

// ============================================
// ENUMS - TEMPLATES ENT_STRUCT
// ============================================

export enum TemplateEntStructEnum {
  // Les 4 fichiers `assistant-manager-d-unite-marchande_v<n>` sont une seule
  // scorecard en 4 versions. Les slugs `_v<n>` ne restent que sur les données
  // d'avant le regroupement (cf. `entStruct/anciensSlugs.ts`).
  ASSISTANT_MANAGER_KFC = "assistant-manager-d-unite-marchande",
  ASSISTANT_MANAGER_KFC_V1 = "assistant-manager-d-unite-marchande_v1",
  ASSISTANT_MANAGER_KFC_V2 = "assistant-manager-d-unite-marchande_v2",
  ASSISTANT_MANAGER_KFC_V3 = "assistant-manager-d-unite-marchande_v3",
  ASSISTANT_MANAGER_KFC_V4 = "assistant-manager-d-unite-marchande_v4",
  CHEF_CHANTIER_FERROVIAIRE_VF = "chef-chantier-ferroviaire_vf",
  CHEF_CHANTIER_FERROVIAIRE_VF_LIGHT = "chef-chantier-ferroviaire_vf_light",
  CADRE_SANTE_V_COURTE = "cadre-de-sante_version-courte",
  SCORECARD_CADRE_SANTE_DEMO = "scorecard_cadre-de-sante_demo",
  SCORECARD_CHEF_PROJET_INFO_DEMO = "scorecard_chef_projet_info_demo",
  SCORECARD_COMPTABLE_DEMO = "scorecard_comptable_demo",
}

export const templateEntStructValidator = v.union(
  v.literal(TemplateEntStructEnum.ASSISTANT_MANAGER_KFC),
  v.literal(TemplateEntStructEnum.ASSISTANT_MANAGER_KFC_V1),
  v.literal(TemplateEntStructEnum.ASSISTANT_MANAGER_KFC_V2),
  v.literal(TemplateEntStructEnum.ASSISTANT_MANAGER_KFC_V3),
  v.literal(TemplateEntStructEnum.ASSISTANT_MANAGER_KFC_V4),
  v.literal(TemplateEntStructEnum.CHEF_CHANTIER_FERROVIAIRE_VF),
  v.literal(TemplateEntStructEnum.CHEF_CHANTIER_FERROVIAIRE_VF_LIGHT),
  v.literal(TemplateEntStructEnum.CADRE_SANTE_V_COURTE),
  v.literal(TemplateEntStructEnum.SCORECARD_CADRE_SANTE_DEMO),
  v.literal(TemplateEntStructEnum.SCORECARD_CHEF_PROJET_INFO_DEMO),
  v.literal(TemplateEntStructEnum.SCORECARD_COMPTABLE_DEMO),
);

// ============================================
// ENUMS - APPLICATIONS
// ============================================
// Une application = une épreuve qu'une organisation peut proposer dans ses
// campagnes. Le super-admin choisit celles de chaque organisation (un groupe
// peut en restreindre la liste) ; le client compose sa campagne parmi elles.
// Remplace les types de parcours (presets de `packages/utils/src/parcoursConfig.ts`).
// Catalogue (libellés, correspondance avec `typeEval` / `template`) :
// `packages/utils/src/applications.ts`.

export enum ApplicationEnum {
  INBASKET = "inbasket", // variante (default / rapide) choisie à la création
  AUTODIAG = "autodiag",
  EGOGRAMME = "egogramme",
  PAMI = "pami",
  COPSOQ3 = "copsoq3",
  FIT_CULTUREL = "pc-fit",
  ENT_STRUCT = "fairview", // scorecard de l'organisation choisie à la création
}

export const applicationValidator = v.union(
  v.literal(ApplicationEnum.INBASKET),
  v.literal(ApplicationEnum.AUTODIAG),
  v.literal(ApplicationEnum.EGOGRAMME),
  v.literal(ApplicationEnum.PAMI),
  v.literal(ApplicationEnum.COPSOQ3),
  v.literal(ApplicationEnum.FIT_CULTUREL),
  v.literal(ApplicationEnum.ENT_STRUCT),
  v.literal(ANCIENNE_APPLICATION_PC_FIT), // legacy, retiré en PR D
  v.literal(ANCIENNE_APPLICATION_FAIRVIEW), // legacy, retiré en PR D
);

// ============================================
// NORMALISATION DES ANCIENS IDENTIFIANTS
// ============================================
// Ancienne valeur → nouvelle. Pas une double lecture : seulement aux frontières
// où une valeur typée large (document ou argument) entre dans du code typé
// strict — création d'évaluations, d'applications, de référentiels. Un front
// pas encore rechargé au déploiement envoie encore les anciennes valeurs.
// Retiré au narrow (PR D).

export function typeEvalCourant(
  typeEval: TypeEvalEnum | typeof ANCIEN_TYPE_EVAL_FAIRVIEW,
): TypeEvalEnum {
  return typeEval === ANCIEN_TYPE_EVAL_FAIRVIEW
    ? TypeEvalEnum.ENT_STRUCT
    : typeEval;
}

export function templateCourant<T extends string | undefined>(
  template: T,
): T {
  if (template === ANCIEN_TEMPLATE_PC_FIT)
    return TemplateFormEnum.FIT_CULTUREL as T;
  if (template === ANCIEN_TEMPLATE_REFERENTIEL_PC_FIT)
    return TemplateFormEnum.REFERENTIEL_FIT_CULTUREL as T;
  return template;
}

export function cibleCourante(
  cible: CibleReferentiel | typeof ANCIENNE_CIBLE_PC_FIT,
): CibleReferentiel {
  return cible === ANCIENNE_CIBLE_PC_FIT ? "pc-fit" : cible;
}

export function applicationCourante(
  application: Infer<typeof applicationValidator>,
): ApplicationEnum {
  if (application === ANCIENNE_APPLICATION_PC_FIT)
    return ApplicationEnum.FIT_CULTUREL;
  if (application === ANCIENNE_APPLICATION_FAIRVIEW)
    return ApplicationEnum.ENT_STRUCT;
  return application;
}

// ============================================
// SCORECARDS ENT_STRUCT (contenu)
// ============================================
// Forme historique des JSON de `convex/entStruct/templates/`, envoyée telle
// quelle au chatbot (`interview_data.competences`) : ne pas la remodeler sans
// accord côté service. Deux variantes coexistent : compétence « plate » (avec
// `questions`) ou hiérarchique (avec `sousCompetences`, v2/v3/v4 KFC).

export const critereScorecardValidator = v.object({
  criteriaId: v.string(),
  description: v.string(),
  // Peut être négatif : pénalité.
  points: v.number(),
  followup: v.string(),
});

export const questionScorecardValidator = v.object({
  questionId: v.string(),
  // Texte libre (« Comp. », « Situa. », ou vide).
  type: v.string(),
  texte: v.string(),
  criteres: v.array(critereScorecardValidator),
});

export const sousCompetenceScorecardValidator = v.object({
  nom: v.string(),
  poids: v.number(),
  questions: v.array(questionScorecardValidator),
});

export const competenceScorecardValidator = v.object({
  nom: v.string(),
  poids: v.number(),
  questions: v.optional(v.array(questionScorecardValidator)),
  sousCompetences: v.optional(v.array(sousCompetenceScorecardValidator)),
});

/** Ce que lisent le chatbot et la notation : titre, description, compétences. */
export const contenuScorecardValidator = v.object({
  titre: v.string(),
  description: v.string(),
  competences: v.array(competenceScorecardValidator),
});

// ============================================
// ENUMS - STATUT DÉTAILLÉ INBASKET
// ============================================

export enum StatutDetailInbasketEnum {
  NON_COMMENCEE = "non_commencee",
  LECTURE_INFORMATIONS_CONTEXTE = "lecture_informations_contexte",
  LECTURE_MAILS = "lecture_mails",
  REPONSE_MAILS = "reponse_mails",
  PRIORISATION_MAILS = "priorisation_mails",
  MAIL_PRESIDENTE = "mail_presidente",
  FINIE = "finie",
  INCOMPLETE = "incomplete",
}

const statutDetailInbasketValidator = v.union(
  v.literal(StatutDetailInbasketEnum.NON_COMMENCEE),
  v.literal(StatutDetailInbasketEnum.LECTURE_INFORMATIONS_CONTEXTE),
  v.literal(StatutDetailInbasketEnum.LECTURE_MAILS),
  v.literal(StatutDetailInbasketEnum.REPONSE_MAILS),
  v.literal(StatutDetailInbasketEnum.PRIORISATION_MAILS),
  v.literal(StatutDetailInbasketEnum.MAIL_PRESIDENTE),
  v.literal(StatutDetailInbasketEnum.FINIE),
  v.literal(StatutDetailInbasketEnum.INCOMPLETE),
);

// ============================================
// ENUMS - STATUT MAIL
// ============================================

export enum StatutMailEnum {
  NON_LU = "non_lu",
  LU = "lu",
  REPONDU = "repondu",
}

const statutMailValidator = v.union(
  v.literal(StatutMailEnum.NON_LU),
  v.literal(StatutMailEnum.LU),
  v.literal(StatutMailEnum.REPONDU),
);

// ============================================
// ENUMS - STATUT DOCUMENT
// ============================================

export enum StatutDocumentEnum {
  NON_LU = "non_lu",
  LU = "lu",
}

const statutDocumentValidator = v.union(
  v.literal(StatutDocumentEnum.NON_LU),
  v.literal(StatutDocumentEnum.LU),
);

// ============================================
// DONNÉES SPÉCIFIQUES PAR TYPE D'ÉVALUATION
// ============================================

// Sous-question autodiag (avec réponse)
const formSousQuestionValidator = v.object({
  name: v.string(),
  label: v.string(),
  reponse: v.string(),
});

// Réponse autodiag (une compétence avec ses sous-questions)
export const formReponseAutodiagValidator = v.object({
  titre: v.optional(v.string()),
  name: v.string(),
  label: v.string(),
  reponse: v.string(),
  sous_questions: v.optional(v.array(formSousQuestionValidator)),
});

// Réponse egogramme (une question oui/non avec profil)
export const formReponseEgogrammeValidator = v.object({
  name: v.string(),
  label: v.string(),
  profil: v.string(),
  response: v.optional(v.boolean()),
});

// Réponse COPSOQ3 (une question avec score brut)
export const formReponseCopsoq3Validator = v.object({
  name: v.string(),
  question: v.string(),
  echelle: v.string(),
  domaine: v.string(),
  reponse: v.string(),
  score: v.number(),
});

// Réponse non scorée COPSOQ3 (partie 1 : profil sociodémographique et
// professionnel ; partie 3 : questions médicales). Hors `reponses` : le calcul
// des échelles COPSOQ ne doit jamais la voir. `question` et `rubrique` sont
// dupliqués pour que la restitution n'ait pas besoin du jeu de questions du
// formulaire.
// `reponse` : libellé (choix), libellés (choix multiple), nombre ou texte.
export const formReponseNonScoreeCopsoq3Validator = v.object({
  name: v.string(),
  rubrique: v.optional(v.string()),
  question: v.string(),
  reponse: v.union(v.string(), v.number(), v.array(v.string())),
});

// Réponses Pami (une liste ordonnée d'items)
export const formReponsePamiValidator = v.object({
  name: v.string(),
  label: v.string(),
  orderedItems: v.array(
    v.object({
      id: v.string(),
      prefixLabel: v.string(),
      label: v.string(),
    }),
  ),
});
// Réponse fit-culturel (un item placé dans la pyramide Q-sort).
// `name` = code STABLE de la dimension (clé de matching avec le référentiel).
// `niveau` = index du niveau dans la pyramide, 0 = le plus fort.
// `score` = nbNiveaux - niveau, dupliqué ici pour que la restitution n'ait pas
// besoin de la pyramide de la campagne pour relire une passation ancienne.
export const formReponseFitCulturelValidator = v.object({
  name: v.string(),
  label: v.string(),
  niveau: v.number(),
  score: v.number(),
});

export const llmResultatValidator = v.object({
  jobId: v.string(),
  app: v.string(),
  timestamp: v.number(),
  data: v.any(),
  // Modèle LLM ayant produit ce brut (multi-modèles ent-struct : un appel
  // groupé renvoie `results_by_model`, chaque entrée est routée vers la
  // notation du modèle correspondant). Absent = brut legacy / mono-modèle.
  model: v.optional(v.string()),
});

const questionsFinalesPamiValidator = v.object({
  langueMaternelle: v.boolean(),
  bonnesConditions: v.boolean(),
  reponsesSinceres: v.boolean(),
});

export const donneesFormValidator = v.object({
  type: v.literal(TypeEvalEnum.FORM),
  template: templateFormValidator,
  reponses: v.record(
    v.string(),
    v.union(
      formReponseAutodiagValidator,
      formReponseEgogrammeValidator,
      formReponseCopsoq3Validator,
      formReponsePamiValidator,
      formReponseFitCulturelValidator,
    ),
  ),
  questionsFinales: v.optional(questionsFinalesPamiValidator),
  // COPSOQ3 : réponses de la partie 1 (profil), dans l'ordre du questionnaire.
  // Absent = passation antérieure à la partie 1.
  profil: v.optional(v.array(formReponseNonScoreeCopsoq3Validator)),
  // COPSOQ3 : réponses de la partie 3 (questions médicales), dans l'ordre.
  // Absent = passation antérieure à la partie 3.
  sante: v.optional(v.array(formReponseNonScoreeCopsoq3Validator)),
  // COPSOQ3 : version du questionnaire, recopiée de la campagne à la création.
  // Pas au candidat : les mutations candidat gardent la valeur stockée. Absent
  // = passation antérieure aux versions, donc pompiers (seule version proposée
  // jusque-là) — lire via `versionCopsoq3DeDonnees`.
  versionCopsoq3: v.optional(versionCopsoq3Validator),
  // Page courante du wizard, sauvegardée à chaque navigation : permet au
  // candidat qui se déconnecte de reprendre là où il s'était arrêté.
  // Optionnel = évaluations antérieures à ce champ reprennent à la page 0.
  pageActuelle: v.optional(v.number()),
});

const donneesInbasketValidator = v.object({
  type: v.literal(TypeEvalEnum.INBASKET),
  template: v.optional(templateInbasketValidator),
  statutDetail: statutDetailInbasketValidator,
  mailSelectionneId: v.optional(v.string()),
  statutsDocuments: v.record(v.string(), statutDocumentValidator),
  reponses: v.record(
    v.string(),
    v.object({
      couleur: v.optional(v.string()),
      statut: statutMailValidator,
      transfertA: v.optional(v.string()),
      important: v.optional(v.boolean()),
      estEnBrouillon: v.optional(v.boolean()),
      annotations: v.optional(v.any()),
      reponse: v.optional(v.string()),
    }),
  ),
});

// ============================================
// DONNÉES TEMPLATE ENTRETIEN STRUCTURÉ
// ============================================

// État runtime d'un critère (tel que retourné par l'API chatbot)
export const entStructCriteriaEtatValidator = v.object({
  criteriaId: v.string(),
  description: v.string(),
  points: v.number(),
  covered: v.boolean(),
  asked: v.boolean(),
});

// Un échange interviewer/candidat rattaché à une question (retourné par l'API
// chatbot dans `questions[].exchanges`, aussi bien pendant l'entretien que dans
// le résultat d'évaluation des critères).
const entStructExchangeValidator = v.object({
  interviewer_text: v.string(),
  candidate_text: v.string(),
  // Qualité de la réponse candidat (ex. "good", "off_topic"). v.string() :
  // énumération pilotée côté chatbot.
  response_quality: v.optional(v.union(v.string(), v.null())),
  // true = échange ignoré lors de l'évaluation des critères
  excluded_from_evaluation: v.optional(v.union(v.boolean(), v.null())),
});

// État runtime d'une question (tel que retourné par l'API chatbot)
export const entStructQuestionEtatValidator = v.object({
  questionId: v.string(),
  competence: v.string(),
  competence_weight: v.optional(v.number()), // Poids de la compétence pour le calcul pondéré
  sous_competence: v.optional(v.union(v.string(), v.null())), // Nom de la sous-compétence (si applicable)
  sous_competence_weight: v.optional(v.union(v.number(), v.null())), // Poids de la sous-compétence pour le calcul pondéré
  question_text: v.string(),
  no_relance: v.optional(v.union(v.boolean(), v.null())),
  criteria: v.array(entStructCriteriaEtatValidator),
  // Échanges de cette question (vide pendant l'entretien tant qu'aucune
  // interaction n'a eu lieu). Optionnel : brutes antérieures à cette clé.
  exchanges: v.optional(v.array(entStructExchangeValidator)),
});

// Métadonnées de transcription pour un message utilisateur
const entStructTranscriptionMetaValidator = v.object({
  caracteresTranscrits: v.number(), // Nombre de caractères venant de la transcription
  caracteresTotal: v.number(), // Nombre total de caractères du message
  pourcentage: v.number(), // Pourcentage (calculé pour faciliter les requêtes)
});

// Métadonnées de détection de triche pour un message utilisateur
const entStructCheatingMetaValidator = v.object({
  // Données temporelles (en millisecondes)
  tempsAffichageQuestion: v.number(), // Temps entre affichage et premier caractère
  tempsRedaction: v.number(), // Temps total de rédaction
  tempsTotal: v.number(), // Temps entre question et envoi

  // Données de frappe
  vitesseFrappe: v.number(), // Caractères par seconde
  nombreEdits: v.number(), // Nombre de modifications
  pasteDetecte: v.boolean(), // Événement paste détecté

  // Données navigateur
  changementsOnglet: v.number(), // Nombre de tab switches
  tempsHorsPage: v.number(), // Temps total hors page (ms)
  absences: v.array(
    v.object({
      debut: v.number(), // Timestamp début absence
      fin: v.number(), // Timestamp fin absence
      duree: v.number(), // Durée en ms
    }),
  ),

  // Longueur du message, nécessaire pour qualifier la vitesse de frappe
  longueurMessage: v.optional(v.number()),

  // Score calculé (0-100)
  scoreSuspicion: v.number(),
});

// Message d'entretien structuré (assistant ou candidat)
const entStructMessageValidator = v.object({
  role: v.union(v.literal("assistant"), v.literal("user")),
  content: v.string(),
  // Contexte d'évaluation au moment du message (uniquement sur messages assistant)
  questionId: v.optional(v.string()),
  criteriaEtat: v.optional(v.array(entStructCriteriaEtatValidator)),
  // Qualité de la réponse évaluée par l'API chatbot (uniquement sur messages
  // utilisateur). Ex. "off_topic". v.string() volontaire : l'énumération est
  // pilotée côté chatbot et peut évoluer sans migration.
  responseQuality: v.optional(v.string()),
  // Métadonnées de transcription (uniquement sur messages utilisateur)
  transcriptionMeta: v.optional(entStructTranscriptionMetaValidator),
  // Métadonnées de détection de triche (uniquement sur messages utilisateur)
  cheatingMeta: v.optional(entStructCheatingMetaValidator),
  // Timestamps pour l'analyse de concentration (uniquement sur messages utilisateur)
  displayedAt: v.optional(v.number()),
  answeredAt: v.optional(v.number()),
  typewriterEndAt: v.optional(v.number()),
  firstResponseAt: v.optional(v.number()),
});

const donneesEntStructValidator = v.object({
  type: v.union(
    v.literal(TypeEvalEnum.ENT_STRUCT),
    v.literal(ANCIEN_TYPE_EVAL_FAIRVIEW), // legacy, retiré en PR D
  ),
  // Slug de la scorecard : libellé et clé de repli, plus une référence. Une
  // copie d'org peut porter un slug hors de TemplateEntStructEnum (`<slug>-2`).
  template: v.optional(v.string()),
  // Version figée de la scorecard, héritée de la campagne. Absente sur les
  // évaluations antérieures à `backfillerScorecardEvaluations` : repli sur le
  // modèle global par slug (`lib/scorecards.ts`).
  scorecardVersionId: v.optional(v.id("scorecardVersions")),
  threadId: v.optional(v.string()),
  reponses: v.record(v.string(), entStructMessageValidator),
  // État global des questions mis à jour à chaque échange
  questionsEtat: v.optional(v.array(entStructQuestionEtatValidator)),
  questionCouranteId: v.optional(v.string()),
});

// Conteneur technique RESULTATS (§1.1) : le candidat ne produit rien, la
// projection d'affichage vit à la racine `evaluations.resultats` (Chantier 2).
// Déclaré P1 ; aucun chemin de création avant un besoin concret P4/P5.
export const donneesResultatsValidator = v.object({
  type: v.literal(TypeEvalEnum.RESULTATS),
});

export const donneesEvaluationValidator = v.union(
  donneesInbasketValidator,
  donneesFormValidator,
  donneesEntStructValidator,
  donneesResultatsValidator,
);

// ============================================
// PROJECTION D'AFFICHAGE (racine evaluations.resultats, §3.2)
// ============================================
// Validator DÉDIÉ (pas llmResultatValidator qui décrit un job unique) : la racine
// porte une PROJECTION calculée depuis les notations. En P1 `data` = blob actuel ;
// en P3 `data` = agrégat multi-évaluateurs (aucune migration de forme ultérieure).
export const evaluationResultatProjectionValidator = v.object({
  kind: v.literal("projection"),
  computedAt: v.number(),
  sourceNotationIds: v.array(v.id("notation")),
  sourceJobIds: v.optional(v.array(v.string())),
  data: v.any(),
});

// ============================================
// RÉFÉRENTIEL ENTREPRISE (plan référentiel §3.2)
// ============================================
// Une dimension = une attente de l'organisation. `code` est la clé STABLE de
// réponse ET de matching : jamais un Id de document (pas de table d'items —
// jointure à chaque restitution + casse silencieuse à la suppression).
export const referentielDimensionValidator = v.object({
  code: v.string(),
  label: v.string(),
  description: v.optional(v.string()),
  // Rubrique d'un socle fit-culturel ANTÉRIEUR, ou d'un item custom
  // d'organisation. Le socle 23 items n'en porte plus.
  categorie: v.optional(v.string()),
  // Regroupement PCfit du socle 23 items (`FIT_CULTUREL_DIMENSIONS`). STOCKÉ,
  // PAS AFFICHÉ : il voyage dans le snapshot pour qu'une restitution par
  // regroupement reste possible sur les campagnes déjà passées.
  dimension: v.optional(v.string()),
  origine: v.union(
    v.literal("socle"),
    v.literal("custom"),
    v.literal("pami"),
  ),
});

// SNAPSHOT, toujours : les dimensions sont copiées en entier dans la campagne à
// la configuration. Renommer/supprimer un item de la banque d'organisation ne
// réécrit jamais la restitution d'une campagne passée.
// Invariants validés côté mutation (pas exprimables au schéma) :
//   qsort      → pyramide === PYRAMIDE_FIT_CULTUREL (2-3-4-5-4-3-2, donc 23 items)
//                (les campagnes configurées avant ce figement portent 2-3-5-3-2 /
//                 15 items : validées à l'écriture d'alors, relues telles quelles)
//   fourchette → min < max
export const configReferentielValidator = v.object({
  // Quel test du candidat ce référentiel pondère.
  cible: v.union(
    v.literal("pc-fit"),
    v.literal("pami"),
    v.literal(ANCIENNE_CIBLE_PC_FIT), // legacy, retiré en PR D
  ),
  dimensions: v.array(referentielDimensionValidator),
  echelle: v.union(
    v.object({
      kind: v.literal("qsort"),
      // Toujours [2,3,4,5,4,3,2] — conservé dans la config (et non lu depuis le
      // code) pour que les passations anciennes ([2,3,5,3,2]) restent relisibles
      // telles quelles.
      pyramide: v.array(v.number()),
    }),
    v.object({
      kind: v.literal("fourchette"),
      min: v.number(), // borne basse de l'échelle (0)
      max: v.number(), // borne haute (10)
    }),
  ),
});

/**
 * Un référentiel rattaché à une campagne. Une campagne peut en porter PLUSIEURS
 * — au plus un par cible : la campagne « recrutement + fit culturel » attend un
 * référentiel PAMI (attentes du poste) ET un référentiel fit-culturel (culture
 * de l'entreprise). Leurs référents, versions et figements sont indépendants.
 *
 * `config.cible` est la CLÉ de l'entrée : elle est unique dans le tableau,
 * invariant vérifié côté mutation (pas exprimable au schéma).
 */
export const referentielCampagneValidator = v.object({
  config: configReferentielValidator,
  // `evaluations` n'a pas de campagneId (le lien passe par parcoursId), et
  // l'éval référentiel n'a PAS de parcours candidat → pointeur explicite.
  evaluationReferentielId: v.id("evaluations"),
});

// ============================================
// SCHEMA
// ============================================

const schema = defineSchema(
  {
    ...authTables,

    // ============================================
    // TABLE - ORGANISATIONS (multi-tenant)
    // ============================================
    organisations: defineTable({
      nom: v.string(),
      // Ancien modèle (clés de presets, vide = tous) : remplacé par
      // `applicationsAutorisees`. Encore écrit en double et relu en repli tant
      // que la prod n'est pas migrée — cf. `lib/applications.ts`.
      parcoursTypesAutorises: v.array(v.string()),
      // Applications proposables dans les campagnes de l'organisation.
      // Optionnel le temps du backfill (`migrations:backfillerApplications`) ;
      // lire via `applicationsDeOrganisation`, jamais directement.
      applicationsAutorisees: v.optional(v.array(applicationValidator)),
      // Version COPSOQ III des campagnes à venir. Absent = universelle ; lire
      // via `versionCopsoq3DeOrganisation`. Les campagnes existantes gardent
      // la leur (figée dans `evaluationsConfig`).
      versionCopsoq3: v.optional(versionCopsoq3Validator),
      archivedAt: v.optional(v.number()),
      // URL publique du logo, saisie par le super-admin (fichier déposé sur
      // Drive). Normalisée à l'écriture par `normaliserUrlLogo` : ce qui est
      // stocké est directement affichable dans un `<img src>`. Optionnel : une
      // org sans logo retombe sur une pastille d'initiales côté portail client.
      logoUrl: v.optional(v.string()),
      // Solde de crédits de l'organisation. Affiché en lecture seule dans le
      // portail client ; seul le super-admin l'édite. Optionnel (orgs existantes
      // créées avant ce champ) → lire `credits ?? 0`. Toute variation passe
      // par `lib/credits.ts`, qui la trace dans `mouvementsCredits`.
      //
      // PEUT ÊTRE NÉGATIF, et ce n'est pas une anomalie : tant que le coût des
      // évaluations n'est pas arrêté, les organisations consomment sans achat
      // et sans blocage. Le solde à découvert EST la mesure de ce qui a été
      // consommé. Ne jamais borner ce champ à zéro.
      credits: v.optional(v.number()),
      // Banque d'items custom PC Fit des campagnes configurées AVANT le
      // figement à 23 items imposés. Plus alimentée : le questionnaire n'est plus
      // composé. Conservée pour relire ces campagnes (§3.4).
      itemsPcFit: v.optional(v.array(referentielDimensionValidator)),
      // Ancien nom de `itemsPcFit`, recopié par
      // `migrations:renommerIdentifiantsOrganisations`. Legacy, retiré en PR D.
      itemsFitCulturel: v.optional(v.array(referentielDimensionValidator)),
    }),

    // ============================================
    // TABLE - JOURNAL DES CRÉDITS
    // ============================================
    // Un document par variation de `organisations.credits`. Le solde reste
    // porté par l'organisation (lecture O(1) dans les portails) ; le journal
    // en explique l'historique. Écrit UNIQUEMENT par `lib/credits.ts`, dans la
    // même transaction que le patch du solde : les deux ne divergent jamais.
    mouvementsCredits: defineTable({
      organisationId: v.id("organisations"),
      type: v.union(
        v.literal("ajout"), // crédit manuel super-admin (achat, geste commercial)
        v.literal("retrait"), // débit manuel super-admin (correction)
        v.literal("debit"), // création d'une évaluation facturable
      ),
      // Variation signée appliquée au solde (négative pour retrait/debit).
      montant: v.number(),
      soldeApres: v.number(),
      motif: v.optional(v.string()),
      // Absent pour un débit déclenché par un candidat (auto-inscription).
      auteurId: v.optional(v.id("users")),
      evaluationId: v.optional(v.id("evaluations")),
    }).index("by_organisationId", ["organisationId"]),

    // ============================================
    // TABLE - GROUPES (sous-ensemble d'une organisation)
    // ============================================
    groups: defineTable({
      nom: v.string(),
      organisationId: v.id("organisations"),
      parcoursTypesAutorises: v.optional(v.array(v.string())),
      // Sous-ensemble des applications de l'organisation. Absent = hérite de
      // l'organisation.
      applicationsAutorisees: v.optional(v.array(applicationValidator)),
    }).index("by_organisationId", ["organisationId"]),

    // ============================================
    // TABLE UTILISATEURS — étendue avec champs multi-tenant
    // ============================================
    users: defineTable({
      name: v.optional(v.string()),
      image: v.optional(v.string()),
      email: v.optional(v.string()),
      emailVerificationTime: v.optional(v.number()),
      phone: v.optional(v.string()),
      phoneVerificationTime: v.optional(v.number()),
      isAnonymous: v.optional(v.boolean()),
      organisationId: v.optional(v.id("organisations")),
      groupId: v.optional(v.id("groups")),
      isAdmin: v.optional(v.boolean()),
      // Rattachement d'avant désactivation. Désactiver retire `organisationId`
      // et `groupId` (c'est ce qui coupe l'accès, via `getCurrentUser`) ; ce
      // champ garde de quoi réactiver le compte à l'identique et le lister
      // parmi les comptes désactivés de son organisation.
      desactivation: v.optional(
        v.object({
          organisationId: v.id("organisations"),
          groupId: v.optional(v.id("groups")),
          le: v.number(),
        }),
      ),
    })
      .index("email", ["email"])
      .index("phone", ["phone"])
      .index("by_organisationId", ["organisationId"])
      .index("by_groupId", ["groupId"])
      .index("by_desactivation_organisationId", ["desactivation.organisationId"]),

    // ============================================
    // TABLE PRINCIPALE - ÉVALUATIONS
    // ============================================
    evaluations: defineTable({
      typeEval: typeEvalValidator,
      statut: statutGlobalValidator,
      template: v.optional(v.string()),
      dateDebut: v.optional(v.number()),
      dateFin: v.optional(v.number()),
      retoursExperience: v.optional(v.record(v.string(), v.string())),
      // Le travail du candidat n'est PAS ici : il vit dans `evaluationDonnees`
      // (phase 2 Database I/O), lu et écrit par `lib/evaluationDepot.ts`.
      // Projection d'affichage calculée depuis les notations (§3.2, chantier 2).
      resultats: v.optional(v.array(evaluationResultatProjectionValidator)),
      jobId: v.optional(v.string()),
      parcoursId: v.optional(v.id("parcoursEvaluations")),
      organisationId: v.optional(v.id("organisations")),
      // Crédits effectivement débités à la soumission, et garde d'idempotence :
      // sa présence dit « déjà facturée ». Une relance de notation repasse
      // l'évaluation en `soumis` et ne doit pas la facturer deux fois. On
      // stocke le montant et non un booléen pour garder la trace du barème
      // appliqué le jour de la soumission, les tarifs n'étant pas figés.
      creditsDebites: v.optional(v.number()),
      // Suppression logique par un super-admin (timestamp). Un document
      // supprimé est invisible partout et ferme les liens candidat/évaluateur ;
      // restaurable depuis la corbeille du portail admin. Une suppression en
      // cascade pose le MÊME timestamp sur les enfants : c'est ce qui permet à
      // la restauration du parent de ne ramener que ce qu'elle a supprimé.
      // Voir `lib/suppression.ts`.
      deletedAt: v.optional(v.number()),
    })
      .index("by_jobId", ["jobId"])
      .index("by_parcoursId", ["parcoursId"])
      .index("by_typeEval_statut", ["typeEval", "statut"])
      .index("by_org_typeEval_statut", [
        "organisationId",
        "typeEval",
        "statut",
      ]),

    // Travail du candidat, réécrit à chaque autosave. Séparé de `evaluations`
    // pour qu'une écriture ici ne ré-exécute pas les queries qui ne lisent que
    // l'en-tête (gardes d'accès, listes, statuts). Un document par évaluation.
    // Accès UNIQUEMENT via `lib/evaluationDepot.ts`.
    evaluationDonnees: defineTable({
      evaluationId: v.id("evaluations"),
      donnees: donneesEvaluationValidator,
    }).index("by_evaluationId", ["evaluationId"]),

    // ============================================
    // TABLE - CAMPAGNES
    // ============================================
    campagnes: defineTable({
      nom: v.string(),
      // Ancien type de parcours (clé de preset) : plus écrit, conservé sur les
      // campagnes historiques. La composition est `evaluationsConfig`.
      typeParcours: v.optional(v.string()),
      statut: statutCampagneValidator,
      dateDebut: v.optional(v.number()),
      dateFin: v.optional(v.number()),
      dateCreation: v.number(),
      dateModification: v.number(),
      evaluationsConfig: v.array(
        v.object({
          typeEval: typeEvalValidator,
          template: v.optional(v.string()),
          // ent_struct : version de scorecard figée pour toute la campagne,
          // recopiée dans chaque évaluation créée.
          scorecardVersionId: v.optional(v.id("scorecardVersions")),
          // copsoq3 : version de l'organisation au moment de la création,
          // figée pour toute la campagne et recopiée dans chaque évaluation.
          // Absent = campagne antérieure aux versions, donc pompiers.
          versionCopsoq3: v.optional(versionCopsoq3Validator),
        }),
      ),
      anonyme: v.optional(v.boolean()),
      progressionGlobale: v.optional(
        v.object({
          nbCandidats: v.number(),
          total: v.number(),
          terminees: v.number(),
          enCours: v.number(),
          nonCommencees: v.number(),
          pourcentage: v.number(),
        }),
      ),
      createdBy: v.optional(v.id("users")),
      organisationId: v.optional(v.id("organisations")),
      groupId: v.optional(v.id("groups")),
      autoriserCreationParcoursCandidats: v.optional(v.boolean()),
      motDePasseCampagne: v.optional(v.string()),

      // Archivage (réversible) — même convention que `organisations.archivedAt` :
      // un timestamp, pas un booléen, pour savoir QUAND. Une campagne archivée
      // sort de la liste admin par défaut et coupe l'accès des candidats comme
      // des évaluateurs (cf. `lib/campagneArchive.ts`). Volontairement PAS un
      // 4ᵉ `statut` : archiver ne doit pas effacer l'info « elle était terminée ».
      archivedAt: v.optional(v.number()),

      // Référentiels entreprise (§3.2/§3.3), au plus un par cible. Absent ou
      // vide = campagne sans référentiel.
      referentiels: v.optional(v.array(referentielCampagneValidator)),

      // Suppression logique par un super-admin (timestamp). Un document
      // supprimé est invisible partout et ferme les liens candidat/évaluateur ;
      // restaurable depuis la corbeille du portail admin. Une suppression en
      // cascade pose le MÊME timestamp sur les enfants : c'est ce qui permet à
      // la restauration du parent de ne ramener que ce qu'elle a supprimé.
      // Voir `lib/suppression.ts`.
      deletedAt: v.optional(v.number()),
    })
      .index("by_groupId", ["groupId"])
      .index("by_organisationId", ["organisationId"])
      .index("by_organisationId_deletedAt", ["organisationId", "deletedAt"])
      .index("by_createdBy", ["createdBy"]),

    // ============================================
    // TABLES - SCORECARDS ENT_STRUCT
    // ============================================
    // En-tête léger (listes, sélecteurs) ; le contenu vit dans des versions
    // immuables. Sans `organisationId` = modèle global du catalogue Evaluans ;
    // avec = copie indépendante propre à une organisation.
    scorecards: defineTable({
      organisationId: v.optional(v.id("organisations")),
      // Figé à la création. Unique par organisation (et parmi les modèles).
      slug: v.string(),
      titre: v.string(),
      description: v.string(),
      // Copie d'org : modèle dont elle est issue (présélection des presets).
      modeleSourceId: v.optional(v.id("scorecards")),
      // Version courante = `scorecardVersions` (scorecardId, numero). Sert
      // aussi de jeton de concurrence à l'enregistrement.
      numeroVersionCourante: v.number(),
      // Version en service : la seule que voient les campagnes. `0` = jamais
      // publiée (brouillon). ABSENT = scorecard antérieure à la publication,
      // lue comme publiée dans sa version courante (`numeroPublie`) jusqu'à
      // `marquerScorecardsPubliees` ; rendu obligatoire au narrow.
      numeroVersionPubliee: v.optional(v.number()),
      // Compteurs monotones : un ID de question (`q<n>`) ou de critère
      // (`c<question>_<n>`) n'est jamais réattribué, même après suppression.
      dernierIdQuestion: v.number(),
      dernierIdCritere: v.number(),
      archivedAt: v.optional(v.number()),
      modifieLe: v.number(),
      modifiePar: v.optional(v.id("users")),
    })
      .index("by_organisationId_and_slug", ["organisationId", "slug"])
      .index("by_organisationId_and_modeleSourceId", [
        "organisationId",
        "modeleSourceId",
      ]),

    // Une ligne par enregistrement, contenu jamais modifié : c'est
    // l'historique, et ce que référencent campagnes et évaluations (snapshot).
    scorecardVersions: defineTable({
      scorecardId: v.id("scorecards"),
      numero: v.number(),
      ...contenuScorecardValidator.fields,
      creeLe: v.number(),
      creePar: v.optional(v.id("users")),
      // Numéro de la version recopiée par une restauration.
      restaureeDepuis: v.optional(v.number()),
      // Publication : seule marque ajoutée après coup, le contenu ne change pas.
      publieeLe: v.optional(v.number()),
      publieePar: v.optional(v.id("users")),
    })
      .index("by_scorecardId_and_numero", ["scorecardId", "numero"])
      // Versions publiées d'une scorecard, sans lire les versions de travail
      // (une par enregistrement) : choix de version à la création de campagne.
      .index("by_scorecardId_and_publieeLe", ["scorecardId", "publieeLe"]),

    // ============================================
    // TABLE PRINCIPALE - PARCOURS D'ÉVALUATIONS
    // ============================================
    parcoursEvaluations: defineTable({
      emailCandidat: v.optional(v.string()),
      nomCandidat: v.optional(v.string()),
      prenomCandidat: v.optional(v.string()),
      // Plus écrits à la création depuis une campagne (cf. `campagnes`) ;
      // conservés sur les parcours historiques.
      typeParcours: v.optional(v.string()),
      slug: v.optional(v.string()),
      titre: v.string(),
      description: v.optional(v.string()),
      dateCreation: v.number(),
      dateModification: v.number(),
      evaluationsIds: v.array(v.id("evaluations")),
      archive: v.optional(v.boolean()),
      campagneId: v.optional(v.id("campagnes")),
      token: v.optional(v.string()),
      anonyme: v.optional(v.boolean()),
      evaluationsCache: v.optional(
        v.array(
          v.object({
            evalId: v.id("evaluations"),
            typeEval: typeEvalValidator,
            template: v.optional(v.string()),
            statut: statutGlobalValidator,
            dateDebut: v.optional(v.number()),
            dateFin: v.optional(v.number()),
          }),
        ),
      ),
      statutCache: v.optional(statutCacheValidator),
      searchText: v.optional(v.string()),
      lienEnvoye: v.optional(v.boolean()),
      createdBy: v.optional(v.id("users")),
      organisationId: v.optional(v.id("organisations")),
      groupId: v.optional(v.id("groups")),
      // Suppression logique par un super-admin (timestamp). Un document
      // supprimé est invisible partout et ferme les liens candidat/évaluateur ;
      // restaurable depuis la corbeille du portail admin. Une suppression en
      // cascade pose le MÊME timestamp sur les enfants : c'est ce qui permet à
      // la restauration du parent de ne ramener que ce qu'elle a supprimé.
      // Voir `lib/suppression.ts`.
      deletedAt: v.optional(v.number()),
    })
      .index("by_campagneId", ["campagneId"])
      .index("by_campagneId_email", ["campagneId", "emailCandidat"])
      .index("by_token", ["token"])
      .index("by_statutCache", ["statutCache"])
      .index("by_organisationId", ["organisationId"])
      .index("by_groupId_statutCache", ["groupId", "statutCache"])
      .index("by_createdBy", ["createdBy"])
      // Parcours « hors campagne » d'une org, sans recollecter tout l'org.
      // Seul consommateur : listerParcoursCampagneVue sans campagneId.
      .index("by_organisationId_campagneId", ["organisationId", "campagneId"])
      .index("by_organisationId_deletedAt", ["organisationId", "deletedAt"]),

    // ============================================
    // TABLE - PARCOURS ÉVALUATEUR (1 token = 1 évaluateur, §2.1 / §3.5)
    // ============================================
    // Réplique la machinerie d'accès de parcoursEvaluations (token/anonymat/nom).
    // L'évaluateur n'est PAS un user Convex : identification par nom.
    parcoursEvaluateur: defineTable({
      campagneId: v.optional(v.id("campagnes")),
      acteur: acteurNotationValidator,

      // Identité & accès — réplique parcoursEvaluations :
      nomEvaluateur: v.optional(v.string()),
      prenomEvaluateur: v.optional(v.string()),
      emailEvaluateur: v.optional(v.string()),
      token: v.optional(v.string()), // défaut = normaliserNom(nomEvaluateur)
      anonyme: v.optional(v.boolean()),
      lienEnvoye: v.optional(v.boolean()),
      // Référent qui EST l'utilisateur du portail client (bouton « Je suis le
      // référent »). Sert à lui rendre son propre lien d'accès sans jamais
      // exposer le token d'un tiers : on ne le renvoie qu'à lui-même.
      referentUserId: v.optional(v.id("users")),

      // Portail évaluateur (humains) : 1 parcoursEvaluateur = 1 PARCOURS regroupant
      // N candidats à noter. Les candidats couverts sont DÉRIVÉS des `notation`
      // (notation.evaluationCible → evaluations.parcoursId). `titre` = libellé du
      // parcours (nom de campagne ou libellé libre). `statutCache` = agrégat.
      titre: v.optional(v.string()),
      statutCache: v.optional(
        v.union(
          v.literal("non_commence"),
          v.literal("en_cours"),
          v.literal("termine"),
        ),
      ),

      dateCreation: v.number(),
      dateModification: v.number(),
      organisationId: v.optional(v.id("organisations")),
      groupId: v.optional(v.id("groups")),
    })
      .index("by_campagneId", ["campagneId"])
      .index("by_token", ["token"])
      .index("by_organisationId", ["organisationId"])
      // Parcours évaluateur « hors campagne » d'une org, sans post-filtrer une
      // page entière. Symétrique de l'index de même nom sur parcoursEvaluations.
      .index("by_organisationId_campagneId", ["organisationId", "campagneId"]),

    // ============================================
    // TABLE - NOTATION (cycle de production d'une note par évaluateur, §3.6)
    // ============================================
    // Source de vérité par évaluateur. Cible TOUJOURS une evaluation.
    notation: defineTable({
      parcoursEvaluateurId: v.id("parcoursEvaluateur"), // propriétaire
      evaluationCible: v.id("evaluations"), // cible — TOUJOURS une évaluation

      statut: statutNotationValidator,
      acteur: acteurNotationValidator,

      // Historique brut par évaluateur (source de vérité, §1.2).
      resultats: v.optional(v.array(llmResultatValidator)),

      // P2/P3 : format normalisé par type/template (base d'agrégation).
      scores: v.optional(v.any()),

      origine: v.optional(v.union(v.literal("backfill"), v.literal("runtime"))),
      jobId: v.optional(v.string()),

      // Réessai automatique du pipeline LLM : nombre de tentatives auto déjà
      // consommées (absent = 0, plafond MAX_TENTATIVES_AUTO). Remis à 0 à chaque
      // succès et à chaque relance manuelle.
      tentativesAuto: v.optional(v.number()),
      // Message d'erreur de la DERNIÈRE tentative (affiché en back-office).
      // Effacé à chaque nouvelle tentative et à chaque succès.
      erreur: v.optional(v.string()),

      organisationId: v.optional(v.id("organisations")),
      dateCreation: v.number(),
      dateModification: v.number(),
    })
      .index("by_evaluationCible", ["evaluationCible"])
      .index("by_parcoursEvaluateurId", ["parcoursEvaluateurId"])
      .index("by_jobId", ["jobId"])
      .index("by_organisationId", ["organisationId"]),

    // ============================================
    // TABLE LIMITE DE TENTATIVES DE CONNEXION
    // ============================================
    tentativesLimiteConnexion: defineTable({
      cle: v.string(),
      tentatives: v.number(),
      premiereTentativeLe: v.number(),
      bloqueJusquA: v.optional(v.number()),
    }).index("by_cle", ["cle"]),
  },
);

export default schema;
